import {
  useWorkspace,
  useWorkspaceFeatureFlags,
} from "src/contexts/global/WorkspaceContext";
import { useAuth0 } from "src/contexts/auth0Provider";
import { hasAdminOrPowerRole } from "src/services/auth";
import { isTeamInsightsAvailableForTeam } from "src/util/isTeamInsightsAvailable";
import { useSelectedOrgChartTeam } from "src/components/shared/TeamMetrics/useTeamMetrics";
import type { OrgChartState } from "../../types";

/** Release: enableOrgDesign. Beta: enableDemoODOptions nested under release.
 * enableOrgChartReportingLineView nests under beta (org-chart canvas flip only;
 * owner/member DnD menus use OD beta). */

type FlagBag = {
  enableOrgDesign?: boolean | null | undefined;
  enableDemoODOptions?: boolean | null | undefined;
  enableOrgChartReportingLineView?: boolean | null | undefined;
  enableDemoODInferredOwners?: boolean | null | undefined;
};

/** OD beta surfaces — requires release + Enable OD beta. */
export const isOrgDesignBetaEnabled = ({
  enableOrgDesign,
  enableDemoODOptions,
}: FlagBag): boolean => Boolean(enableOrgDesign && enableDemoODOptions);

export const useOrgDesignBetaEnabled = (): boolean => {
  const { enableOrgDesign, enableDemoODOptions } = useWorkspaceFeatureFlags();
  return isOrgDesignBetaEnabled({ enableOrgDesign, enableDemoODOptions });
};

/** Org-chart reporting-line view + DnD — OD beta nested flag. */
export const isOrgChartReportingLineViewEnabled = ({
  enableOrgDesign,
  enableDemoODOptions,
  enableOrgChartReportingLineView,
}: FlagBag): boolean =>
  Boolean(
    isOrgDesignBetaEnabled({ enableOrgDesign, enableDemoODOptions }) &&
    enableOrgChartReportingLineView
  );

export const useOrgChartReportingLineViewEnabled = (): boolean => {
  const {
    enableOrgDesign,
    enableDemoODOptions,
    enableOrgChartReportingLineView,
  } = useWorkspaceFeatureFlags();
  return isOrgChartReportingLineViewEnabled({
    enableOrgDesign,
    enableDemoODOptions,
    enableOrgChartReportingLineView,
  });
};

export const useDateDiffFeatureEnabled = (): boolean => {
  const { enableShowDiffInOrgChart } = useWorkspaceFeatureFlags();
  return enableShowDiffInOrgChart;
};

export const useTeamInsightsFeatureEnabled = (): boolean => {
  const { enableOrgDesign } = useWorkspaceFeatureFlags();
  return Boolean(enableOrgDesign);
};

/** Inferred owner stand-ins — OD beta + nested inferred-owners flag. */
export const useShowInferredOwnersFeatureEnabled = (): boolean => {
  const { enableOrgDesign, enableDemoODOptions, enableDemoODInferredOwners } =
    useWorkspaceFeatureFlags();
  return Boolean(
    isOrgDesignBetaEnabled({ enableOrgDesign, enableDemoODOptions }) &&
    enableDemoODInferredOwners
  );
};

export const useChangelogTabEnabled = (): boolean => {
  const { enableOrgDesign } = useWorkspaceFeatureFlags();
  return Boolean(enableOrgDesign);
};

export const useMetricsPanelEnabled = (state: OrgChartState): boolean => {
  const isFeatureEnabled = useTeamInsightsFeatureEnabled();
  const workspace = useWorkspace();
  const { currentTeam } = useSelectedOrgChartTeam(state);
  if (!currentTeam?.id || !currentTeam?.type) {
    return false;
  }
  return isTeamInsightsAvailableForTeam({
    insightsEnabled: isFeatureEnabled,
    team: currentTeam,
    groupTypes: workspace?.config?.groupTypes,
  });
};

type AuthUser = Parameters<typeof hasAdminOrPowerRole>[0];

export const isOrgChartEditModeControlsEnabled = ({
  enableOrgDesign,
  user,
}: {
  enableOrgDesign: boolean;
  user: AuthUser;
}): boolean => Boolean(enableOrgDesign) && hasAdminOrPowerRole(user);

export const useOrgChartEditModeControlsEnabled = (): boolean => {
  const { enableOrgDesign } = useWorkspaceFeatureFlags();
  const { user } = useAuth0();
  return isOrgChartEditModeControlsEnabled({ enableOrgDesign, user });
};
